import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuoteService, GetCustomerLinkResponse, SendCustomerLinkRequest, SendCustomerLinkResponse } from '../../services/quote.service';

export interface EmailQuoteModalData {
  quoteId: number;
  customerName: string;
  customerEmail?: string;
}

@Component({
  selector: 'app-email-quote-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  template: `
    <div class="email-modal-container">
      <div mat-dialog-title class="modal-title-row">
        <div class="modal-title-text">
          <mat-icon>link</mat-icon>
          Share Quote Link
        </div>
        <button mat-icon-button type="button" (click)="onCancel()" [disabled]="isSending" aria-label="Close" class="modal-close">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="modal-content">
        <div *ngIf="isLoadingLink" class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading customer link...</p>
        </div>

        <div *ngIf="!isLoadingLink">
          <!-- Customer Link Section -->
          <div class="link-container" *ngIf="customerLink">
            <label class="link-label">Customer Quote Link:</label>
            <div class="link-input-container">
              <input
                type="text"
                [value]="customerLink"
                readonly
                class="link-input"
                #linkInput>
              <button
                mat-icon-button
                color="primary"
                (click)="copyLink(linkInput)"
                matTooltip="Copy Link"
                class="copy-button">
                <mat-icon>content_copy</mat-icon>
              </button>
            </div>
            <p class="link-description">Copy this link to share with <strong>{{ data.customerName }}</strong> so they can view their quote online.</p>
          </div>

          <!-- Email Form Section -->
          <div class="email-section" *ngIf="!emailSent">
            <div class="divider">
              <span>OR</span>
            </div>

            <p class="modal-description">
              Send the link directly to <strong>{{ data.customerName }}</strong> via email.
            </p>

            <form [formGroup]="emailForm" class="email-form">
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Customer Email</mat-label>
                <input matInput
                       type="email"
                       formControlName="customerEmail"
                       placeholder="customer@example.com"
                       required>
                <mat-error *ngIf="emailForm.get('customerEmail')?.hasError('required')">
                  Email is required
                </mat-error>
                <mat-error *ngIf="emailForm.get('customerEmail')?.hasError('email')">
                  Please enter a valid email address
                </mat-error>
              </mat-form-field>

              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Customer Name</mat-label>
                <input matInput
                       formControlName="customerName"
                       placeholder="Customer Name"
                       required>
                <mat-error *ngIf="emailForm.get('customerName')?.hasError('required')">
                  Customer name is required
                </mat-error>
              </mat-form-field>
            </form>
          </div>

          <!-- Email Result Section -->
          <div *ngIf="emailSent" class="email-result">
            <div class="success-icon">
              <mat-icon [class]="emailSentSuccessfully ? 'success' : 'warning'">
                {{ emailSentSuccessfully ? 'check_circle' : 'warning' }}
              </mat-icon>
            </div>

            <h3 class="result-title">
              {{ emailSentSuccessfully ? 'Email Sent Successfully!' : 'Link Generated' }}
            </h3>

            <p class="result-message">
              <ng-container *ngIf="emailSentSuccessfully">
                The quote link has been sent to the customer's email address.
              </ng-container>
              <ng-container *ngIf="!emailSentSuccessfully">
                Email failed to send, but the quote link has been generated. You can copy it above.
              </ng-container>
            </p>
          </div>
        </div>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()" [disabled]="isSending || isLoadingLink">
          {{ emailSent ? 'Close' : 'Close' }}
        </button>
        <button *ngIf="!emailSent && !isLoadingLink"
                mat-raised-button
                color="primary"
                (click)="onSendEmail()"
                [disabled]="!emailForm.valid || isSending">
          <mat-spinner *ngIf="isSending" diameter="20" class="button-spinner"></mat-spinner>
          <mat-icon *ngIf="!isSending">send</mat-icon>
          {{ isSending ? 'Sending...' : 'Send Email' }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .email-modal-container {
      width: 100%;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .modal-description {
      margin: 0 0 24px;
      color: #6f6a60;
      line-height: 1.5;
    }

    .modal-content {
      padding-top: 8px;
      overflow: auto;
      min-width: 0;
    }

    .email-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .full-width {
      width: 100%;
    }

    .button-spinner {
      margin-right: 8px;
    }

    .modal-title-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      margin: 0;
      padding: 0 0 8px;
    }

    .modal-title-text {
      display: flex;
      align-items: center;
      gap: 8px;
      min-height: 40px;
      font-size: 1.75rem;
      line-height: 1.2;
    }

    .modal-close {
      flex-shrink: 0;
      align-self: flex-start;
      margin: 0;
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
      margin: 0;
    }

    .email-result {
      text-align: center;
      padding: 20px 0;
    }

    .success-icon {
      margin-bottom: 16px;
    }

    .success-icon mat-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
    }

    .success-icon .success {
      color: #4caf50;
    }

    .success-icon .warning {
      color: #ff9800;
    }

    .result-title {
      margin: 0 0 12px 0;
      font-size: 20px;
      font-weight: 500;
      color: #333;
    }

    .result-message {
      margin: 0 0 24px 0;
      color: #666;
      line-height: 1.5;
    }

    .link-container {
      text-align: left;
      background: #f3efe6;
      padding: 16px;
      border-radius: 12px;
      border: 1px solid #dfd7c5;
    }

    .link-label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      color: #4e483d;
      font-size: 14px;
    }

    .link-input-container {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(255, 255, 255, 0.7);
      border: 1px solid #d8cfbd;
      border-radius: 10px;
      padding: 6px 8px 6px 10px;
    }

    .link-input {
      flex: 1;
      min-width: 0;
      padding: 10px 0;
      border: 0;
      font-family: monospace;
      font-size: 12px;
      background: transparent;
      color: #3f3a31;
    }

    .link-input:focus {
      outline: none;
    }

    .copy-button {
      flex-shrink: 0;
      color: #8d7a4f;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      gap: 16px;
    }

    .loading-container p {
      margin: 0;
      color: #666;
    }

    .link-description {
      margin-top: 12px;
      margin-bottom: 0;
      font-size: 12px;
      color: #6f6a60;
    }

    .email-section {
      margin-top: 24px;
    }

    .divider {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 24px 0;
      color: #999;
    }

    .divider::before,
    .divider::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #e0e0e0;
    }

    .divider span {
      padding: 0 16px;
      font-size: 12px;
      font-weight: 500;
    }

    @media (max-width: 640px) {
      .email-modal-container {
        width: 100%;
      }

      .modal-title-text {
        font-size: 1.5rem;
      }
    }
  `]
})
export class EmailQuoteModalComponent implements OnInit {
  emailForm: FormGroup;
  isSending = false;
  isLoadingLink = true;
  emailSent = false;
  customerLink = '';
  emailSentSuccessfully = false;

  constructor(
    private fb: FormBuilder,
    private quoteService: QuoteService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<EmailQuoteModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailQuoteModalData
  ) {
    this.emailForm = this.fb.group({
      customerEmail: [data.customerEmail || '', [Validators.required, Validators.email]],
      customerName: [data.customerName || '', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Pre-fill with existing data if available
    if (this.data.customerEmail) {
      this.emailForm.patchValue({
        customerEmail: this.data.customerEmail
      });
    }

    // Fetch customer link when modal opens
    this.fetchCustomerLink();
  }

  fetchCustomerLink(): void {
    this.isLoadingLink = true;
    this.quoteService.getCustomerLink(this.data.quoteId).subscribe({
      next: (response: GetCustomerLinkResponse) => {
        this.isLoadingLink = false;
        this.customerLink = response.customerLink;
      },
      error: (error) => {
        this.isLoadingLink = false;
        console.error('Error fetching customer link:', error);
        this.snackBar.open('Failed to load customer link. Please try again.', 'Close', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      }
    });
  }

  onSendEmail(): void {
    if (this.emailForm.valid) {
      this.isSending = true;

      const request: SendCustomerLinkRequest = {
        customerEmail: this.emailForm.value.customerEmail,
        customerName: this.emailForm.value.customerName
      };

      this.quoteService.sendCustomerLink(this.data.quoteId, request).subscribe({
        next: (response: SendCustomerLinkResponse) => {
          this.isSending = false;
          this.emailSent = true;
          // Update link if different (shouldn't be, but just in case)
          if (response.customerLink && response.customerLink !== this.customerLink) {
            this.customerLink = response.customerLink;
          }
          this.emailSentSuccessfully = response.emailSent;

          if (response.emailSent) {
            this.snackBar.open('Quote link sent successfully!', 'Close', {
              duration: 3000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['success']
            });
          } else {
            this.snackBar.open('Link generated but email failed to send. You can copy the link below.', 'Close', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['warning']
            });
          }
        },
        error: (error) => {
          this.isSending = false;
          console.error('Error sending customer link:', error);
          this.snackBar.open('Failed to send quote link. Please try again.', 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['error']
          });
        }
      });
    }
  }

  copyLink(inputElement: HTMLInputElement): void {
    inputElement.select();
    inputElement.setSelectionRange(0, 99999); // For mobile devices

    try {
      document.execCommand('copy');
      this.snackBar.open('Link copied to clipboard!', 'Close', {
        duration: 2000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['success']
      });
    } catch (err) {
      // Fallback for browsers that don't support execCommand
      navigator.clipboard.writeText(this.customerLink).then(() => {
        this.snackBar.open('Link copied to clipboard!', 'Close', {
          duration: 2000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success']
        });
      }).catch(() => {
        this.snackBar.open('Failed to copy link. Please copy manually.', 'Close', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error']
        });
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
